import { BASE_URL } from '../../global';

export const TUTOR_TOKEN_KEY = 'tutorAuthToken';
export const ADMIN_SHELL_TOKEN_KEY = 'tutorAdminShellToken';
export const ADMIN_MODE_KEY = 'tutorAdminMode';
export const ACT_AS_KEY = 'viewAsTutorKey';
export const ACT_AS_NAME_KEY = 'actAsTutorName';
export const IS_TUTOR_ADMIN_KEY = 'is_tutor_admin';
export const TUT_KEY_BACKUP_KEY = 'tutorAdminTutKeyBackup';
export const TUTOR_ADMIN_VIEW_CHANGED = 'tutor-admin-view-changed';
export const TUTOR_ADMIN_FLAG_CHANGED = 'tutor-admin-flag-changed';
export const ADMIN_SHELL_BLOCKED_MESSAGE = 'Select a tutor before performing this action';

export function getAuthToken() {
  const raw = localStorage.getItem('authUser');
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return raw;
  }
}

function parseStoredToken(raw) {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    return raw;
  }
}

/** Original login tutor token; used for profile, notifications, checkUser, Exit Admin. */
export function getTutorAuthToken() {
  if (isAdminMode()) {
    const saved = parseStoredToken(localStorage.getItem(TUTOR_TOKEN_KEY));
    if (saved) {
      return saved;
    }
  }
  return getAuthToken();
}

/** Admin shell token for /tutor-admin/tutors and /tutor-admin/view-as while impersonating. */
export function getAdminShellToken() {
  if (!isAdminMode()) {
    return null;
  }

  const shell = parseStoredToken(localStorage.getItem(ADMIN_SHELL_TOKEN_KEY));
  if (shell) {
    return shell;
  }

  // Before a tutor is selected, authUser is still the admin shell.
  if (!getViewAsTutorKey()) {
    return getAuthToken();
  }

  return null;
}

function readJwtClaim(token, claim) {
  if (!token || typeof token !== 'string') {
    return undefined;
  }

  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return undefined;
    }

    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return decoded?.[claim];
  } catch (error) {
    return undefined;
  }
}

/** Prefer localStorage (refreshed from checkUser / DB) over stale JWT claim. */
export function isTutorAdminEnabled() {
  const stored = localStorage.getItem(IS_TUTOR_ADMIN_KEY);
  if (stored === '1') {
    return true;
  }
  if (stored === '0') {
    return false;
  }

  const claim = readJwtClaim(getTutorAuthToken(), 'is_tutor_admin');
  return claim === true || claim === 1 || claim === '1';
}

export function isAdminMode() {
  return localStorage.getItem(ADMIN_MODE_KEY) === '1';
}

export function getViewAsTutorKey() {
  return localStorage.getItem(ACT_AS_KEY);
}

export function getActAsTutorName() {
  return localStorage.getItem(ACT_AS_NAME_KEY);
}

/** True when admin shell is active but no tutor has been selected yet. */
export function shouldBlockWorkApi() {
  return isAdminMode() && !getViewAsTutorKey();
}

/** Tutor key for LCMS and other tutor-scoped APIs (impersonated tutor when acting). */
export function getEffectiveTutorKey() {
  const viewAs = getViewAsTutorKey();
  if (viewAs) {
    return viewAs;
  }

  return localStorage.getItem('tut_key');
}

export function applyWorkAuthHeader(axiosInstance) {
  const token = getAuthToken();
  if (token) {
    axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
  return token;
}

export function isAdminShellBlockedError(error) {
  return (
    error?.response?.status === 403 &&
    error?.response?.data?.error === ADMIN_SHELL_BLOCKED_MESSAGE
  );
}

export function persistLoginFlags(loginRow) {
  const isAdmin = parseInt(loginRow?.is_tutor_admin, 10) === 1 ? '1' : '0';
  localStorage.setItem(IS_TUTOR_ADMIN_KEY, isAdmin);
  localStorage.removeItem(TUTOR_TOKEN_KEY);
  localStorage.removeItem(ADMIN_SHELL_TOKEN_KEY);
  localStorage.removeItem(ADMIN_MODE_KEY);
  localStorage.removeItem(ACT_AS_KEY);
  localStorage.removeItem(ACT_AS_NAME_KEY);
  localStorage.removeItem(TUT_KEY_BACKUP_KEY);
}

/** Update capability flag from checkUser (DB). Does not clear admin session unless revoked. */
export function syncTutorAdminFlag(loginRow) {
  const isAdmin = parseInt(loginRow?.is_tutor_admin, 10) === 1 ? '1' : '0';
  const previous = localStorage.getItem(IS_TUTOR_ADMIN_KEY);
  localStorage.setItem(IS_TUTOR_ADMIN_KEY, isAdmin);

  if (previous !== isAdmin) {
    if (isAdmin === '0' && isAdminMode()) {
      exitAdminMode();
    }
    window.dispatchEvent(new Event(TUTOR_ADMIN_FLAG_CHANGED));
  }
}

export function enterAdminMode(adminToken) {
  const currentToken = localStorage.getItem('authUser');
  if (currentToken) {
    localStorage.setItem(TUTOR_TOKEN_KEY, currentToken);
  }

  const currentTutKey = localStorage.getItem('tut_key');
  if (currentTutKey) {
    localStorage.setItem(TUT_KEY_BACKUP_KEY, currentTutKey);
  }

  localStorage.setItem('authUser', JSON.stringify(adminToken));
  localStorage.setItem(ADMIN_SHELL_TOKEN_KEY, JSON.stringify(adminToken));
  localStorage.setItem(ADMIN_MODE_KEY, '1');
  localStorage.removeItem(ACT_AS_KEY);
  localStorage.removeItem(ACT_AS_NAME_KEY);
  window.dispatchEvent(new Event(TUTOR_ADMIN_VIEW_CHANGED));
}

/** Store full-access impersonation token for the selected tutor. */
export function setViewAsTutor(impersonationToken, tutorKey, tutorName) {
  localStorage.setItem('authUser', JSON.stringify(impersonationToken));
  localStorage.setItem(ACT_AS_KEY, tutorKey);
  localStorage.setItem(ACT_AS_NAME_KEY, tutorName || '');
  if (tutorKey) {
    localStorage.setItem('tut_key', tutorKey);
  }
  window.dispatchEvent(new Event(TUTOR_ADMIN_VIEW_CHANGED));
}

export function exitAdminMode() {
  const tutorToken = localStorage.getItem(TUTOR_TOKEN_KEY);
  const backedUpTutKey = localStorage.getItem(TUT_KEY_BACKUP_KEY);

  if (tutorToken) {
    localStorage.setItem('authUser', tutorToken);
  }

  if (backedUpTutKey) {
    localStorage.setItem('tut_key', backedUpTutKey);
  }

  localStorage.removeItem(TUTOR_TOKEN_KEY);
  localStorage.removeItem(ADMIN_SHELL_TOKEN_KEY);
  localStorage.removeItem(ADMIN_MODE_KEY);
  localStorage.removeItem(ACT_AS_KEY);
  localStorage.removeItem(ACT_AS_NAME_KEY);
  localStorage.removeItem(TUT_KEY_BACKUP_KEY);
  window.dispatchEvent(new Event(TUTOR_ADMIN_VIEW_CHANGED));
}

export function getSessionListUrl() {
  return `${BASE_URL}session/getAllSessionList`;
}

export function getUpcomingSessionListUrl() {
  return `${BASE_URL}session/getAllUpcommingSessionList`;
}

export function getHomeCardDetailsUrl() {
  return `${BASE_URL}home/cardDetails`;
}

export function getHomeCalendarDetailsUrl() {
  return `${BASE_URL}home/calendarDetails`;
}

export function getStudentApiUrl(path) {
  return `${BASE_URL}student/${path}`;
}
